import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Form, Row, Col, Button as BSButton } from 'react-bootstrap';
import { Alert } from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import { getMembers, createMember, updateMember, deleteMember, getCurrentLeaveMembers } from '../services/api';
import { User } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { getDefaultAvatarByGender } from '../assets/avatars';
import { resolveUploadAssetUrl } from '../utils/resolveUploadAssetUrl';
import { LimsNavIcon } from '../components/LimsNavIcon';
import { getDemoMembers } from '../demo/demoBrowseMocks';
import './MemberManagement.css';

type MemberTypeOption = NonNullable<User['studentType']>;
type MemberTypeFilter = 'all' | MemberTypeOption;

const MEMBER_TYPE_OPTIONS: MemberTypeOption[] = ['研究生', '博士', '博士后', '教师'];

const PAGE_SIZE = 10;

const MemberManagement: React.FC = () => {
  const { role, isDemoBrowseOnly } = useAuth();
  const [members, setMembers] = useState<User[]>([]);
  const [teachers, setTeachers] = useState<User[]>([]);
  const [leaveMemberMap, setLeaveMemberMap] = useState<
    Record<string, { type?: 'leave' | 'trip'; leaveStart?: string; leaveEnd?: string }>
  >({});
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingMember, setViewingMember] = useState<User | null>(null);
  const [editingMember, setEditingMember] = useState<User | null>(null);
  const [formData, setFormData] = useState<Partial<User>>({
    name: '',
    gender: '男',
    studentId: '',
    phone: '',
    email: '',
    supervisor: '',
    enrollmentYear: new Date().getFullYear(),
    studentType: '研究生',
    role: '普通用户'
  });
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState<{ type: 'success' | 'danger'; message: string } | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [memberTypeFilter, setMemberTypeFilter] = useState<MemberTypeFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const fetchMembers = async () => {
    if (isDemoBrowseOnly) {
      const demo = getDemoMembers();
      setMembers(demo);
      setTeachers(demo.filter((member: User) => member.studentType === '教师'));
      setLeaveMemberMap({});
      return;
    }
    try {
      const [membersResponse, leaveResponse] = await Promise.all([getMembers(), getCurrentLeaveMembers()]);
      if (membersResponse.success) {
        setMembers(membersResponse.data || []);
        setTeachers(membersResponse.data?.filter((member: User) => member.studentType === '教师') || []);
      }
      if (leaveResponse.success && leaveResponse.data) {
        setLeaveMemberMap(leaveResponse.data);
      } else {
        setLeaveMemberMap({});
      }
    } catch (error) {
      console.error('获取成员列表失败:', error);
      setBanner({ type: 'danger', message: '获取成员列表失败' });
    }
  };

  useEffect(() => {
    void fetchMembers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDemoBrowseOnly]);

  const activeMembers = useMemo(
    () => members.filter((m) => !m.isDeleted),
    [members]
  );

  const filteredMembers = useMemo(() => {
    let list = activeMembers;
    if (memberTypeFilter !== 'all') {
      list = list.filter((m) => m.studentType === memberTypeFilter);
    }
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (m) =>
          m.name?.toLowerCase().includes(q) ||
          m.studentId?.toLowerCase().includes(q) ||
          m.email?.toLowerCase().includes(q) ||
          m.phone?.includes(q) ||
          m.supervisor?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [activeMembers, memberTypeFilter, searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [memberTypeFilter, searchQuery]);

  useEffect(() => {
    const maxP = Math.max(1, Math.ceil(filteredMembers.length / PAGE_SIZE));
    setPage((p) => Math.min(p, maxP));
  }, [filteredMembers.length]);

  const totalPages = Math.max(1, Math.ceil(filteredMembers.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageSlice = useMemo(() => {
    const start = (safePage - 1) * PAGE_SIZE;
    return filteredMembers.slice(start, start + PAGE_SIZE);
  }, [filteredMembers, safePage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (role !== '管理员' && role !== '超级管理员') {
      setBanner({ type: 'danger', message: '您没有权限执行此操作' });
      return;
    }

    setLoading(true);

    try {
      if (!formData.name?.trim()) {
        setBanner({ type: 'danger', message: '请输入姓名' });
        setLoading(false);
        return;
      }
      if (!formData.studentId?.trim()) {
        setBanner({ type: 'danger', message: '请输入学号/工号' });
        setLoading(false);
        return;
      }
      if (!formData.gender) {
        setBanner({ type: 'danger', message: '请选择性别' });
        setLoading(false);
        return;
      }

      if (editingMember) {
        const response = await updateMember(editingMember._id!, formData, selectedFile || undefined);
        if (response.success) {
          setBanner({ type: 'success', message: '成员信息更新成功' });
          await fetchMembers();
          setShowModal(false);
          resetForm();
        } else {
          setBanner({ type: 'danger', message: response.error || '更新失败' });
        }
      } else {
        const response = await createMember(formData, selectedFile || undefined);
        if (response.success) {
          setBanner({ type: 'success', message: '成员添加成功，已自动创建账号（默认密码：111111）' });
          await fetchMembers();
          setShowModal(false);
          resetForm();
        } else {
          setBanner({ type: 'danger', message: response.error || '添加成员失败' });
        }
      }
    } catch (error) {
      console.error('操作失败:', error);
      setBanner({ type: 'danger', message: editingMember ? '更新成员失败' : '添加成员失败' });
    } finally {
      setLoading(false);
    }
  };

  const handleView = (member: User) => {
    setViewingMember(member);
    setShowViewModal(true);
  };

  const handleEdit = (member: User) => {
    setEditingMember(member);
    setFormData(member);
    setSelectedFile(null);
    setShowModal(true);
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleDelete = async (member: User) => {
    if (window.confirm(`确定要删除成员 ${member.name} 吗？删除后数据将在1个月后彻底清除。`)) {
      try {
        const response = await deleteMember(member._id!);
        if (response.success) {
          setBanner({ type: 'success', message: response.data?.message || '成员删除成功，数据将在1个月后彻底清除' });
          await fetchMembers();
        }
      } catch (error) {
        console.error('删除成员失败:', error);
        setBanner({ type: 'danger', message: '删除成员失败' });
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      gender: '男',
      studentId: '',
      phone: '',
      email: '',
      supervisor: '',
      enrollmentYear: new Date().getFullYear(),
      studentType: '研究生',
      role: '普通用户',
      avatar: ''
    });
    setEditingMember(null);
    setSelectedFile(null);
    const fileInput = document.querySelector('input[type="file"]') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const handleAddMember = () => {
    resetForm();
    setShowModal(true);
  };

  const avatarSrc = (member: User) => {
    const a = member.avatar;
    if (!a) return getDefaultAvatarByGender(member.gender);
    if (a.startsWith('blob:')) return a;
    return resolveUploadAssetUrl(a) || a || getDefaultAvatarByGender(member.gender);
  };

  const identityLine = (member: User) =>
    [member.studentType, member.studentId].filter(Boolean).join(' · ') || '—';

  /** 列表「状态」圆点：请假/出差与头像角标语义一致 */
  const statusDotKind = (
    member: User,
    leave?: { type?: 'leave' | 'trip'; leaveStart?: string; leaveEnd?: string }
  ): 'active' | 'inactive' | 'leave' | 'trip' => {
    if (member.isDeleted) return 'inactive';
    if (leave?.type === 'trip') return 'trip';
    if (leave) return 'leave';
    return 'active';
  };

  const canManage = role === '管理员' || role === '超级管理员';

  const rangeStart = filteredMembers.length === 0 ? 0 : (safePage - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(safePage * PAGE_SIZE, filteredMembers.length);

  const goPrevPage = () => setPage((p) => Math.max(1, p - 1));
  const goNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  /** 与请假/出差接口中的 applicantId 对齐（部分账号仅有 id） */
  const leaveInfoForMember = (member: User) => {
    const id = String(member._id || member.id || '');
    if (!id) return undefined;
    return leaveMemberMap[id];
  };

  const leaveBadgeText = (info: { type?: 'leave' | 'trip' }, format: 'full' | 'short') => {
    if (info.type === 'trip') return format === 'short' ? '差' : '出差';
    return format === 'short' ? '假' : '请假';
  };

  const systemRoleBadge = (r: User['role']) => {
    if (r === '超级管理员') return '超级管理员';
    if (r === '管理员') return '管理员';
    return '普通成员';
  };

  const supervisorInitials = (name: string | undefined) => {
    const t = name?.trim();
    if (!t) return '?';
    if (/[\u4e00-\u9fff]/.test(t)) return t.slice(0, 2);
    const parts = t.split(/\s+/).filter(Boolean);
    if (parts.length >= 2) return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    return t.slice(0, 2).toUpperCase();
  };

  const formatProfileTimestamp = (iso: string | undefined) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('zh-CN', { dateStyle: 'medium', timeStyle: 'short' });
    } catch {
      return '—';
    }
  };

  return (
    <div className="page-container full-height members-lims">
      <nav className="mm-crumb" aria-label="breadcrumb">
        <HomeOutlined />
        <span>首页</span>
        <span className="mm-crumb-sep">/</span>
        <span>成员</span>
      </nav>

      {banner && (
        <Alert
          type={banner.type === 'success' ? 'success' : 'error'}
          closable
          showIcon
          onClose={() => setBanner(null)}
          message={banner.message}
          className="mm-banner"
        />
      )}

      <header className="mm-page-head">
        <div>
          <h1 className="mm-title">成员目录</h1>
          <p className="mm-subtitle">
            管理人员档案、登录凭证与访问权限；与实验室角色策略保持一致。
          </p>
        </div>
        {canManage && (
          <button type="button" className="mm-btn-primary" onClick={handleAddMember}>
            <LimsNavIcon name="person_add" sizePx={20} />
            添加成员
          </button>
        )}
      </header>

      <div className="mm-table-shell">
        <div className="mm-table-toolbar">
          <div className="mm-filter-row">
            <span className="mm-filter-label">筛选：</span>
            <div className="mm-filter-pills">
              <button
                type="button"
                className={`mm-pill ${memberTypeFilter === 'all' ? 'is-active' : ''}`}
                onClick={() => setMemberTypeFilter('all')}
              >
                全部
              </button>
              {MEMBER_TYPE_OPTIONS.map((t) => (
                <button
                  key={t}
                  type="button"
                  className={`mm-pill ${memberTypeFilter === t ? 'is-active' : ''}`}
                  onClick={() => setMemberTypeFilter(t)}
                >
                  {t}
                </button>
              ))}
            </div>
          </div>
          <div className="mm-search">
            <LimsNavIcon name="search" sizePx={18} className="mm-search-icon" />
            <input
              type="search"
              className="mm-search-input"
              placeholder="搜索姓名、学号、邮箱…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="搜索成员"
            />
          </div>
          <div className="mm-toolbar-meta">
            显示 <strong>{rangeStart}-{rangeEnd}</strong> / 共 {filteredMembers.length} 人
            <div className="mm-mini-nav">
              <button type="button" aria-label="上一页" onClick={goPrevPage} disabled={safePage <= 1}>
                <LimsNavIcon name="chevron_left" sizePx={20} />
              </button>
              <button type="button" aria-label="下一页" onClick={goNextPage} disabled={safePage >= totalPages}>
                <LimsNavIcon name="chevron_right" sizePx={20} />
              </button>
            </div>
          </div>
        </div>

        <div className="mm-sheet-scroll">
          <table className="mm-sheet">
            <thead>
              <tr>
                <th>姓名与身份</th>
                <th>成员类型</th>
                <th>导师</th>
                <th>联系方式</th>
                <th style={{ textAlign: 'center' }}>状态</th>
                <th className="mm-th-actions">操作</th>
              </tr>
            </thead>
            <tbody>
              {pageSlice.length === 0 ? (
                <tr>
                  <td colSpan={6} className="mm-empty">
                    暂无匹配的成员
                  </td>
                </tr>
              ) : (
                pageSlice.map((member) => {
                  const leaveInfo = leaveInfoForMember(member);
                  const dotKind = statusDotKind(member, leaveInfo);
                  const badgeLabel = leaveInfo ? leaveBadgeText(leaveInfo, 'full') : '';
                  const leaveTip = leaveInfo?.leaveStart && leaveInfo?.leaveEnd
                    ? `${badgeLabel}中（${leaveInfo.leaveStart} ~ ${leaveInfo.leaveEnd}）`
                    : `${badgeLabel}中`;
                  const dotTitle =
                    dotKind === 'inactive'
                      ? '已停用'
                      : dotKind === 'leave' || dotKind === 'trip'
                        ? leaveTip
                        : '正常';
                  return (
                    <tr key={member._id || member.username} className="mm-row">
                      <td>
                        <div className="mm-identity">
                          <span className="mm-identity-avatar-wrap">
                            <img
                              className="mm-identity-avatar"
                              src={avatarSrc(member)}
                              alt=""
                              onError={(e) => {
                                e.currentTarget.src = getDefaultAvatarByGender(member.gender);
                              }}
                            />
                            {leaveInfo && (
                              <span
                                className={`mm-leave-badge${leaveInfo.type === 'trip' ? ' mm-leave-badge--trip' : ''}`}
                                title={leaveTip}
                              >
                                {badgeLabel}
                              </span>
                            )}
                          </span>
                          <div>
                            <p className="mm-identity-name">{member.name}</p>
                            <p className="mm-identity-sub">{identityLine(member)}</p>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="mm-role mm-role--member">{member.studentType?.trim() || '—'}</span>
                      </td>
                      <td className="mm-supervisor-cell">{member.supervisor?.trim() || '—'}</td>
                      <td>
                        <div className="mm-contact">
                          <p className="mm-contact-mail">{member.email || '—'}</p>
                          <p className="mm-contact-phone">{member.phone || '—'}</p>
                        </div>
                      </td>
                      <td>
                        <div className="mm-status">
                          <span className={`mm-status-dot mm-status-dot--${dotKind}`} title={dotTitle} />
                        </div>
                      </td>
                      <td>
                        <div className="mm-actions">
                          <button type="button" className="mm-icon-action" aria-label="查看" onClick={() => handleView(member)}>
                            <LimsNavIcon name="visibility" sizePx={22} />
                          </button>
                          {canManage && (
                            <>
                              <button type="button" className="mm-icon-action" aria-label="编辑" onClick={() => handleEdit(member)}>
                                <LimsNavIcon name="edit_note" sizePx={22} />
                              </button>
                              <button
                                type="button"
                                className="mm-icon-action mm-icon-action--danger"
                                aria-label="删除"
                                onClick={() => handleDelete(member)}
                              >
                                <LimsNavIcon name="delete" sizePx={22} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="mm-table-footer">
          <span>
            本页 <strong>{pageSlice.length}</strong> 条 · 共 <strong>{filteredMembers.length}</strong> 条
          </span>
          <div className="mm-pagination">
            <button type="button" className="mm-page-step" disabled={safePage <= 1} onClick={goPrevPage}>
              上一页
            </button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let num: number;
              if (totalPages <= 5) num = i + 1;
              else if (safePage <= 3) num = i + 1;
              else if (safePage >= totalPages - 2) num = totalPages - 4 + i;
              else num = safePage - 2 + i;
              return (
                <button
                  key={num}
                  type="button"
                  className={`mm-page-btn ${safePage === num ? 'is-current' : ''}`}
                  onClick={() => setPage(num)}
                >
                  {num}
                </button>
              );
            })}
            <button
              type="button"
              className="mm-page-step mm-page-step--primary"
              disabled={safePage >= totalPages}
              onClick={goNextPage}
            >
              下一页
            </button>
          </div>
        </div>
      </div>

      {/* 查看成员 — Clinical Curator 档案弹窗（对齐 stitch 设计稿） */}
      <Modal
        show={showViewModal}
        onHide={() => setShowViewModal(false)}
        centered
        dialogClassName="mm-profile-modal__dialog"
        contentClassName="mm-profile-modal__content"
        backdropClassName="mm-profile-modal__backdrop"
      >
        {viewingMember && (() => {
          const vLeave = leaveInfoForMember(viewingMember);
          const vLabel = vLeave ? leaveBadgeText(vLeave, 'full') : '';
          const vTip =
            vLeave?.leaveStart && vLeave?.leaveEnd
              ? `${vLabel}中（${vLeave.leaveStart} ~ ${vLeave.leaveEnd}）`
              : vLeave
                ? `${vLabel}中`
                : '';
          const compliance = viewingMember.isDeleted
            ? { key: 'inactive' as const, label: '已停用', pill: 'INACTIVE' }
            : vLeave?.type === 'trip'
              ? { key: 'trip' as const, label: '出差中', pill: 'TRAVEL' }
              : vLeave
                ? { key: 'leave' as const, label: '请假中', pill: 'AWAY' }
                : { key: 'active' as const, label: '在岗合规', pill: 'ACTIVE' };

          return (
            <>
              <div className="mm-profile-modal__topbar">
                <span className="mm-profile-modal__kicker">Member Profile</span>
                <button
                  type="button"
                  className="mm-profile-modal__close"
                  onClick={() => setShowViewModal(false)}
                  aria-label="关闭"
                >
                  <LimsNavIcon name="close" sizePx={20} />
                </button>
              </div>
              <Modal.Body className="mm-profile-modal__body">
                <div className="mm-profile-modal__hero">
                  <div className="mm-profile-modal__avatar-stack">
                    <div className="mm-profile-modal__avatar-ring">
                      <img
                        src={avatarSrc(viewingMember)}
                        alt=""
                        className="mm-profile-modal__avatar-img"
                        onError={(e) => {
                          e.currentTarget.src = getDefaultAvatarByGender(viewingMember.gender);
                        }}
                      />
                    </div>
                    {vLeave && (
                      <span
                        className={`mm-leave-badge mm-profile-modal__leave-corner${
                          vLeave.type === 'trip' ? ' mm-leave-badge--trip' : ''
                        }`}
                        title={vTip}
                      >
                        {vLabel}
                      </span>
                    )}
                    <span
                      className={`mm-profile-modal__verified${compliance.key === 'inactive' ? ' mm-profile-modal__verified--muted' : ''}`}
                      title={compliance.key === 'active' ? '账号正常' : compliance.label}
                    >
                      <LimsNavIcon
                        name={compliance.key === 'inactive' ? 'block' : 'verified'}
                        sizePx={14}
                        style={
                          compliance.key === 'active'
                            ? ({ fontVariationSettings: "'FILL' 1" } as React.CSSProperties)
                            : undefined
                        }
                      />
                    </span>
                  </div>
                  <div className="mm-profile-modal__hero-text">
                    <h2 className="mm-profile-modal__name">{viewingMember.name}</h2>
                    <div className="mm-profile-modal__badges">
                      <span className="mm-profile-modal__badge mm-profile-modal__badge--primary">
                        {viewingMember.studentType?.trim() || '未标注类型'}
                      </span>
                      <span className="mm-profile-modal__badge mm-profile-modal__badge--secondary">
                        {systemRoleBadge(viewingMember.role)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="mm-profile-modal__divider">
                  <span>Information Details</span>
                </div>

                <div className="mm-profile-modal__grid">
                  <div className="mm-profile-modal__col">
                    <div className="mm-profile-modal__field">
                      <span className="mm-profile-modal__label">用户名 (Username)</span>
                      <div className="mm-profile-modal__value-row">
                        <LimsNavIcon name="alternate_email" sizePx={17} className="mm-profile-modal__field-icon" />
                        <span>{viewingMember.username || '—'}</span>
                      </div>
                    </div>
                    <div className="mm-profile-modal__field">
                      <span className="mm-profile-modal__label">性别 (Gender)</span>
                      <div className="mm-profile-modal__value-row">
                        <LimsNavIcon name="person" sizePx={17} className="mm-profile-modal__field-icon" />
                        <span>{viewingMember.gender}</span>
                      </div>
                    </div>
                    <div className="mm-profile-modal__field">
                      <span className="mm-profile-modal__label">学号/工号 (ID Number)</span>
                      <div className="mm-profile-modal__value-row">
                        <LimsNavIcon name="badge" sizePx={17} className="mm-profile-modal__field-icon" />
                        <span>{viewingMember.studentId || '—'}</span>
                      </div>
                    </div>
                    <div className="mm-profile-modal__field">
                      <span className="mm-profile-modal__label">手机号 (Phone)</span>
                      <div className="mm-profile-modal__value-row">
                        <LimsNavIcon name="smartphone" sizePx={17} className="mm-profile-modal__field-icon" />
                        <span>{viewingMember.phone?.trim() || '未填写'}</span>
                      </div>
                    </div>
                    <div className="mm-profile-modal__field">
                      <span className="mm-profile-modal__label">邮箱 (Email)</span>
                      <div className="mm-profile-modal__value-row">
                        <LimsNavIcon name="mail" sizePx={17} className="mm-profile-modal__field-icon" />
                        <span className="mm-profile-modal__mono">{viewingMember.email?.trim() || '未填写'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mm-profile-modal__col mm-profile-modal__col--accent">
                    <div className="mm-profile-modal__card">
                      <div className="mm-profile-modal__field mm-profile-modal__field--tight">
                        <span className="mm-profile-modal__label">成员类型 (Member Type)</span>
                        <div className="mm-profile-modal__value-row mm-profile-modal__value-row--strong">
                          <LimsNavIcon
                            name="biotech"
                            sizePx={17}
                            className="mm-profile-modal__field-icon mm-profile-modal__field-icon--fill"
                            style={{ fontVariationSettings: "'FILL' 1" } as React.CSSProperties}
                          />
                          <span>{viewingMember.studentType?.trim() || '—'}</span>
                        </div>
                      </div>
                      <div className="mm-profile-modal__field mm-profile-modal__field--tight">
                        <span className="mm-profile-modal__label">导师 (Mentor)</span>
                        <div className="mm-profile-modal__value-row">
                          <span className="mm-profile-modal__sup-avatar" aria-hidden>
                            {supervisorInitials(viewingMember.supervisor)}
                          </span>
                          <span>{viewingMember.supervisor?.trim() || '—'}</span>
                        </div>
                      </div>
                      <div className="mm-profile-modal__field mm-profile-modal__field--tight">
                        <span className="mm-profile-modal__label">入学年份 (Enrollment)</span>
                        <div className="mm-profile-modal__value-row">
                          <LimsNavIcon name="calendar_today" sizePx={17} className="mm-profile-modal__field-icon" />
                          <span>{viewingMember.enrollmentYear ?? '—'}</span>
                        </div>
                      </div>
                    </div>

                    <div className={`mm-profile-modal__compliance mm-profile-modal__compliance--${compliance.key}`}>
                      <div className="mm-profile-modal__compliance-left">
                        <LimsNavIcon name="shield" sizePx={17} className="mm-profile-modal__compliance-shield" />
                        <span className="mm-profile-modal__compliance-title">Compliance Status</span>
                      </div>
                      <span className="mm-profile-modal__compliance-pill">{compliance.pill}</span>
                    </div>
                  </div>
                </div>

                <div className="mm-profile-modal__meta">
                  <div className="mm-profile-modal__timestamps">
                    <div className="mm-profile-modal__ts">
                      <LimsNavIcon name="history" sizePx={14} />
                      <span>
                        创建时间 (Created): {formatProfileTimestamp(viewingMember.createdAt)}
                      </span>
                    </div>
                    <div className="mm-profile-modal__ts">
                      <LimsNavIcon name="update" sizePx={14} />
                      <span>
                        更新时间 (Updated): {formatProfileTimestamp(viewingMember.updatedAt)}
                      </span>
                    </div>
                  </div>
                  <div className="mm-profile-modal__actions">
                    <button
                      type="button"
                      className="mm-profile-modal__btn mm-profile-modal__btn--secondary"
                      onClick={() => setShowViewModal(false)}
                    >
                      关闭
                    </button>
                    {canManage && (
                      <button
                        type="button"
                        className="mm-profile-modal__btn mm-profile-modal__btn--primary"
                        onClick={() => {
                          setShowViewModal(false);
                          handleEdit(viewingMember);
                        }}
                      >
                        编辑档案
                      </button>
                    )}
                  </div>
                </div>
              </Modal.Body>
              <div className="mm-profile-modal__notice">
                <LimsNavIcon name="info" sizePx={16} className="mm-profile-modal__notice-icon" />
                <p>仅授权课题负责人与行政管理员可查看完整人事与合规信息。</p>
              </div>
            </>
          );
        })()}
      </Modal>

      {/* 添加/编辑成员模态框 */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingMember ? '编辑成员' : '添加成员'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>姓名 *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>性别</Form.Label>
                  <Form.Select
                    value={formData.gender}
                    onChange={(e) => setFormData({ ...formData, gender: e.target.value as '男' | '女' | '其他' })}
                  >
                    <option value="男">男</option>
                    <option value="女">女</option>
                    <option value="其他">其他</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>头像</Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    onChange={(e) => {
                      const target = e.target as HTMLInputElement;
                      const file = target.files?.[0];
                      if (file) {
                        setSelectedFile(file);
                        const fileUrl = URL.createObjectURL(file);
                        setFormData({ ...formData, avatar: fileUrl });
                      } else {
                        setSelectedFile(null);
                        setFormData({ ...formData, avatar: '' });
                      }
                    }}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <div className="mb-3">
                  <Form.Label>头像预览</Form.Label>
                  <div
                    className="border rounded p-2"
                    style={{
                      width: '100px',
                      height: '100px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#f8f9fa'
                    }}
                  >
                    <img
                      src={
                        !formData.avatar
                          ? '/default-avatar.svg'
                          : formData.avatar.startsWith('blob:')
                            ? formData.avatar
                            : resolveUploadAssetUrl(formData.avatar) ||
                              formData.avatar ||
                              getDefaultAvatarByGender(formData.gender)
                      }
                      alt=""
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                      onError={(e) => {
                        e.currentTarget.src = '/default-avatar.svg';
                      }}
                    />
                  </div>
                </div>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>学号/工号 *</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.studentId}
                    onChange={(e) => setFormData({ ...formData, studentId: e.target.value })}
                    required
                    placeholder="将作为登录用户名"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>联系电话</Form.Label>
                  <Form.Control
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>邮箱</Form.Label>
                  <Form.Control
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>导师</Form.Label>
                  <Form.Select
                    value={formData.supervisor}
                    onChange={(e) => setFormData({ ...formData, supervisor: e.target.value })}
                    disabled={formData.studentType === '教师'}
                  >
                    <option value="">请选择导师</option>
                    {teachers.map((teacher) => (
                      <option key={teacher._id} value={teacher.name}>
                        {teacher.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>入学年份</Form.Label>
                  <Form.Control
                    type="number"
                    value={formData.enrollmentYear}
                    onChange={(e) => setFormData({ ...formData, enrollmentYear: parseInt(e.target.value) })}
                    min="2000"
                    max="2030"
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>成员类型</Form.Label>
                  <Form.Select
                    value={formData.studentType}
                    onChange={(e) => {
                      const newStudentType = e.target.value as '研究生' | '博士' | '博士后' | '教师';
                      setFormData({
                        ...formData,
                        studentType: newStudentType,
                        supervisor: newStudentType === '教师' ? '' : formData.supervisor,
                        role:
                          newStudentType === '教师' && role === '超级管理员'
                            ? '管理员'
                            : newStudentType === '教师'
                              ? '普通用户'
                              : formData.role
                      });
                    }}
                  >
                    <option value="研究生">研究生</option>
                    <option value="博士">博士</option>
                    <option value="博士后">博士后</option>
                    <option value="教师">教师</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>系统角色</Form.Label>
              <Form.Select
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value as '普通用户' | '管理员' | '超级管理员' })}
                disabled={role !== '超级管理员'}
              >
                <option value="普通用户">普通用户</option>
                {(role === '超级管理员' || (role === '管理员' && formData.role === '管理员')) && (
                  <option value="管理员">管理员</option>
                )}
                {role === '超级管理员' && <option value="超级管理员">超级管理员</option>}
              </Form.Select>
              {role !== '超级管理员' && (
                <Form.Text className="text-muted">只有超级管理员可以修改成员的系统角色</Form.Text>
              )}
            </Form.Group>

            {!editingMember && (
              <Alert
                type="info"
                showIcon
                message={
                  <span>
                    添加成员后将自动创建登录账号，默认密码为 <code>111111</code>，用户名使用学号/工号。
                  </span>
                }
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <BSButton variant="secondary" onClick={() => setShowModal(false)}>
              取消
            </BSButton>
            <BSButton variant="primary" type="submit" disabled={loading}>
              {loading ? '处理中...' : editingMember ? '更新' : '添加'}
            </BSButton>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default MemberManagement;
